import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective } from 'ngx-mask';
import { ClientService } from '../../../core/api/client.service';
import { DateInputComponent } from '../../../shared/components/date-input/date-input.component';
import { toApiDate } from '../../../shared/utils/date.utils';

@Component({
  selector: 'app-client-edit',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule,
    MatCardModule, MatIconModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatSnackBarModule, MatProgressSpinnerModule,
    NgxMaskDirective,
    DateInputComponent,
  ],
  templateUrl: './client-edit.component.html',
  styleUrl: './client-edit.component.scss',
})
export class ClientEditComponent implements OnInit {
  form: FormGroup;
  loading = true;
  submitting = false;
  private clientId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private clientService: ClientService,
    private snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      phone: ['', Validators.required],
      email: [''],
      driverLicense: [''],
      driverLicenseDate: [null],
      passportNumber: [''],
      passportIssuedDate: [null],
      passportIssuedBy: [''],
      birthDate: [null],
      address: [''],
      notes: [''],
    });
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.router.navigate(['/clients']);
      return;
    }
    this.clientId = id;
    this.clientService.get(id).subscribe({
      next: (c) => {
        this.form.patchValue({
          firstName: c.first_name,
          middleName: c.middle_name || '',
          lastName: c.last_name,
          phone: c.phone,
          email: c.email || '',
          driverLicense: c.driver_license || '',
          driverLicenseDate: c.driver_license_date ?? null,
          passportNumber: c.passport_number || '',
          passportIssuedDate: c.passport_issued_date ?? null,
          passportIssuedBy: c.passport_issued_by || '',
          birthDate: c.birth_date ?? null,
          address: c.address || '',
          notes: c.notes || '',
        });
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Клиент не найден', 'ОК', { duration: 3000 });
        this.router.navigate(['/clients']);
      },
    });
  }

  goBack(): void {
    if (this.clientId) {
      this.router.navigate(['/clients', this.clientId]);
    } else {
      this.router.navigate(['/clients']);
    }
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting || !this.clientId) return;
    this.submitting = true;
    const v = this.form.value;
    const data = {
      first_name: v.firstName,
      middle_name: v.middleName || '',
      last_name: v.lastName,
      phone: v.phone,
      email: v.email || '',
      driver_license: v.driverLicense || '',
      driver_license_date: v.driverLicenseDate ? toApiDate(new Date(v.driverLicenseDate)) : null,
      passport_number: v.passportNumber || '',
      passport_issued_date: v.passportIssuedDate ? toApiDate(new Date(v.passportIssuedDate)) : null,
      passport_issued_by: v.passportIssuedBy || '',
      birth_date: v.birthDate ? toApiDate(new Date(v.birthDate)) : null,
      address: v.address || '',
      notes: v.notes || '',
    };
    this.clientService.update(this.clientId, data).subscribe({
      next: () => {
        this.snackBar.open('Клиент сохранён', 'ОК', { duration: 3000 });
        this.router.navigate(['/clients', this.clientId]);
      },
      error: (err: any) => {
        this.submitting = false;
        const errorData = err.error;
        if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
          this.setServerErrors(errorData);
          const nonField = errorData.all || errorData.non_field_errors;
          if (nonField) {
            const msg = Array.isArray(nonField) ? nonField[0] : String(nonField);
            this.snackBar.open(msg, 'ОК', { duration: 5000 });
          }
        } else {
          const msg = Array.isArray(errorData) ? errorData[0] : 'Ошибка сохранения';
          this.snackBar.open(msg, 'ОК', { duration: 5000 });
        }
      },
    });
  }

  private readonly apiFieldToControl: Record<string, string> = {
    first_name: 'firstName',
    middle_name: 'middleName',
    last_name: 'lastName',
    phone: 'phone',
    email: 'email',
    driver_license: 'driverLicense',
    driver_license_date: 'driverLicenseDate',
    passport_number: 'passportNumber',
    passport_issued_date: 'passportIssuedDate',
    passport_issued_by: 'passportIssuedBy',
    birth_date: 'birthDate',
    address: 'address',
    notes: 'notes',
  };

  private setServerErrors(errors: Record<string, any>): void {
    for (const [apiField, controlName] of Object.entries(this.apiFieldToControl)) {
      const control = this.form.get(controlName);
      if (control && errors.hasOwnProperty(apiField)) {
        const messages = errors[apiField];
        const msg = Array.isArray(messages) ? messages[0] : String(messages);
        control.setErrors({ ...control.errors, serverError: msg });
      }
    }
  }
}
